import { useState, useEffect, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, ChevronUp, ChevronDown, Trash2, Plus } from 'lucide-react'
import { service } from '../lib/api/service'
import type { Project, Surcharge } from '../lib/api/types'
import {
  STATUS_PIPELINE, ANCHOR_STATUS_IDS, resolveStatuses, defaultStatusId,
  statusDotColor, type StatusStep, type StatusConfig,
} from '../utils/statusUtils'
import { useProjectConfig } from '../lib/appContext'

/** upplagd/fakturerad bookend the pipeline and are always shown (see filterPipeline). */
const UNHIDEABLE_STATUS_IDS = ['upplagd', 'fakturerad']
const NEW_STATUS_HUE = 220
const NEW_STATUS_CHROMA = 0.1

export default function ProjectSettings() {
  const navigate = useNavigate()
  const { setStatusConfig } = useProjectConfig()
  const [project, setProject]     = useState<Project | null>(null)
  const [surcharges, setSurcharges] = useState<Surcharge[]>([])
  const [statuses, setStatuses]   = useState<StatusStep[]>(STATUS_PIPELINE)
  const [localDefault, setLocalDefault] = useState<string>('upplagd')
  const [localHidden, setLocalHidden] = useState<string[]>([])
  const [loading, setLoading]     = useState(true)
  const [saving, setSaving]       = useState(false)
  const [saved, setSaved]         = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([service.getProject(), service.getSurcharges()])
      .then(([p, s]) => {
        setProject(p)
        setSurcharges(s)
        const cfg: StatusConfig = p?.statusConfig ? JSON.parse(p.statusConfig) : {}
        setStatuses(resolveStatuses(cfg))
        setLocalDefault(defaultStatusId(cfg))
        setLocalHidden(cfg.hidden ?? [])
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading || !project) return <div className="loading"><div className="spinner" /></div>

  const set = (field: keyof Project, value: string | number | null) =>
    setProject(p => (p ? ({ ...p, [field]: value }) as Project : p))

  const updateSurcharge = (sortOrder: number, field: 'typ' | 'procent', value: string) =>
    setSurcharges(prev => prev.map(s =>
      s.sortOrder === sortOrder
        ? ({ ...s, [field]: field === 'procent' ? parseFloat(value) / 100 || 0 : value }) as Surcharge
        : s
    ))

  const updateStatus = (id: string, patch: Partial<StatusStep>) =>
    setStatuses(prev => prev.map(s => (s.id === id ? { ...s, ...patch } : s)))

  const moveStatus = (index: number, dir: -1 | 1) =>
    setStatuses(prev => {
      const next = [...prev]
      const target = index + dir
      if (target < 0 || target >= next.length) return prev
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })

  const addStatus = () =>
    setStatuses(prev => {
      let n = 1
      while (prev.some(s => s.id === `status-${n}`)) n++
      const id = `status-${n}`
      return [...prev, { id, kort: 'Ny status', label: 'Ny status', hue: NEW_STATUS_HUE, c: NEW_STATUS_CHROMA }]
    })

  const removeStatus = (id: string) => {
    setStatuses(prev => prev.filter(s => s.id !== id))
    setLocalHidden(prev => prev.filter(h => h !== id))
    if (localDefault === id) setLocalDefault('upplagd')
  }

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const config = { statuses, defaultStatus: localDefault, hidden: localHidden }
      await Promise.all([
        service.saveProject({ ...project, statusConfig: JSON.stringify(config) }),
        service.saveSurcharges(surcharges),
      ])
      setStatusConfig(config)
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  const p = project

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projekt</h1>
        </div>
        <div className="page-actions">
          <button className="icon-btn" onClick={() => navigate('/print/projekt')}>
            <Printer size={14} /> Skriv ut
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar…' : 'Spara ändringar'}
          </button>
        </div>
      </div>

      {saved && <div className="notification notification-success"><span>Inställningarna sparades.</span></div>}
      {saveError && <div className="notification notification-error"><span>Fel: {saveError}</span></div>}

      {/* 2-column grid */}
      <div className="settings-grid">

        {/* 01 Grunddata */}
        <SettingsCard nr="01" title="Grunddata">
          <div className="grid-2">
            <Field label="Projektnummer" mono>
              <input className="field-input" value={p.projektnr ?? ''} onChange={e => set('projektnr', e.target.value)} placeholder="t.ex. 2024-001" />
            </Field>
            <Field label="Status">
              <select className="field-input" value={p.projektStatus ?? ''} onChange={e => set('projektStatus', e.target.value)}>
                <option value="">–</option>
                <option value="Pågående">Pågående</option>
                <option value="Avslutat">Avslutat</option>
                <option value="Planerat">Planerat</option>
              </select>
            </Field>
          </div>
          <Field label="Projektnamn" full>
            <input className="field-input" value={p.projektNamn ?? ''} onChange={e => set('projektNamn', e.target.value)} placeholder="Projektets officiella namn" />
          </Field>
          <div className="grid-2">
            <Field label="Ort">
              <input className="field-input" value={p.ort ?? ''} onChange={e => set('ort', e.target.value)} placeholder="Stad" />
            </Field>
            <Field label="Adress">
              <input className="field-input" value={p.adress ?? ''} onChange={e => set('adress', e.target.value)} />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Startdatum" mono>
              <input className="field-input" type="date" value={p.startdatum ?? ''} onChange={e => set('startdatum', e.target.value)} />
            </Field>
            <Field label="Slutdatum" mono>
              <input className="field-input" type="date" value={p.slutdatum ?? ''} onChange={e => set('slutdatum', e.target.value)} />
            </Field>
          </div>
          <Field label="Kort beskrivning" full>
            <textarea className="field-input" rows={2} value={p.beskrivning ?? ''} onChange={e => set('beskrivning', e.target.value)} placeholder="Kort projektbeskrivning" />
          </Field>
        </SettingsCard>

        {/* 02 Ekonomi & avtal */}
        <SettingsCard nr="02" title="Ekonomi & avtal">
          <Field label="Kontraktssumma" mono>
            <input className="field-input" type="number" value={p.kontraktssumma ?? ''} onChange={e => set('kontraktssumma', e.target.value === '' ? null : parseFloat(e.target.value))} placeholder="0" />
          </Field>
          <div className="grid-2">
            <Field label="Betalningsvillkor">
              <input className="field-input" value={p.betalningsvillkor ?? ''} onChange={e => set('betalningsvillkor', e.target.value)} placeholder="t.ex. 30 dagar netto" />
            </Field>
            <Field label="Avtalsform">
              <input className="field-input" value={p.avtalsform ?? ''} onChange={e => set('avtalsform', e.target.value)} placeholder="ABT 06" />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Räntesats vid dröjsmål">
              <input className="field-input" value={p.rantesats ?? ''} onChange={e => set('rantesats', e.target.value)} placeholder="t.ex. 8%" />
            </Field>
            <Field label="Garantitid">
              <input className="field-input" value={p.garantitid ?? ''} onChange={e => set('garantitid', e.target.value)} placeholder="t.ex. 5 år" />
            </Field>
          </div>
          {/* Påslagstabell */}
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-faint)', marginBottom: 8 }}>
              Påslagstabell
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--ink-faint)', marginBottom: 10 }}>
              Kod 1–8, används vid prissättning i ÄTA-specifikationer.
            </div>
            <table style={{ width: '100%', fontSize: 12.5 }}>
              <thead>
                <tr style={{ background: 'var(--bg-2)' }}>
                  <th style={{ width: 40, textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 11 }}>Kod</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, fontSize: 11 }}>Benämning</th>
                  <th style={{ width: 100, textAlign: 'right', padding: '6px 8px', fontWeight: 600, fontSize: 11 }}>Procent</th>
                </tr>
              </thead>
              <tbody>
                {surcharges.map(s => (
                  <tr key={s.sortOrder}>
                    <td style={{ padding: '4px 8px', fontFamily: 'var(--mono)', color: 'var(--ink-faint)' }}>{s.sortOrder}</td>
                    <td style={{ padding: '4px 8px' }}>
                      <input
                        className="field-input"
                        style={{ padding: '3px 7px', fontSize: 12.5 }}
                        value={s.typ ?? ''}
                        onChange={e => updateSurcharge(s.sortOrder, 'typ', e.target.value)}
                        placeholder={`Påslag ${s.sortOrder}`}
                      />
                    </td>
                    <td style={{ padding: '4px 8px', textAlign: 'right' }}>
                      <input
                        className="field-input"
                        type="number"
                        step="0.01"
                        style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 12.5, padding: '3px 7px', width: 80 }}
                        value={s.procent ? +(s.procent * 100).toFixed(4) : ''}
                        onChange={e => updateSurcharge(s.sortOrder, 'procent', e.target.value)}
                        placeholder="0.00"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SettingsCard>

        {/* 03 Beställare */}
        <SettingsCard nr="03" title="Beställare">
          <Field label="Företag" full>
            <input className="field-input" value={p.bestellare ?? ''} onChange={e => set('bestellare', e.target.value)} placeholder="Beställarens företagsnamn" />
          </Field>
          <div className="grid-2">
            <Field label="Kontaktperson">
              <input className="field-input" value={p.bestRef ?? ''} onChange={e => set('bestRef', e.target.value)} />
            </Field>
            <Field label="Roll">
              <input className="field-input" value={p.bestKontakt ?? ''} onChange={e => set('bestKontakt', e.target.value)} placeholder="t.ex. Projektledare" />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="E-post">
              <input className="field-input" type="email" value={p.bestEmail ?? ''} onChange={e => set('bestEmail', e.target.value)} placeholder="namn@foretag.se" />
            </Field>
            <Field label="Telefon" mono>
              <input className="field-input" value={p.bestTelefon ?? ''} onChange={e => set('bestTelefon', e.target.value)} />
            </Field>
          </div>
        </SettingsCard>

        {/* 04 Entreprenör */}
        <SettingsCard nr="04" title="Entreprenör">
          <Field label="Företag" full>
            <input className="field-input" value={p.entreforetag ?? ''} onChange={e => set('entreforetag', e.target.value)} placeholder="Tibram Bygg AB" />
          </Field>
          <div className="grid-2">
            <Field label="Platschef">
              <input className="field-input" value={p.platschef ?? ''} onChange={e => set('platschef', e.target.value)} />
            </Field>
            <Field label="Arbetsledare">
              <input className="field-input" value={p.arbetsledare ?? ''} onChange={e => set('arbetsledare', e.target.value)} />
            </Field>
          </div>
          <div className="grid-2">
            <Field label="Org.nr" mono>
              <input className="field-input" value={p.orgNr ?? ''} onChange={e => set('orgNr', e.target.value)} placeholder="556xxx-xxxx" />
            </Field>
            <Field label="Telefon platskontor" mono>
              <input className="field-input" value={p.telPlatskontor ?? ''} onChange={e => set('telPlatskontor', e.target.value)} />
            </Field>
          </div>
        </SettingsCard>
      </div>

      {/* 06 Statusar (full width) */}
      <SettingsCard nr="06" title="Statusar" fullWidth>
        <div style={{ fontSize: 12.5, color: 'var(--ink-dim)', marginBottom: 14 }}>
          Anpassa statusflödet: byt namn, färg och ordning, lägg till egna steg eller dölj steg du inte använder.
          Upplagd, Godkänd och Fakturerad är inbyggda och kan inte tas bort. Upplagd och Fakturerad kan inte döljas.
        </div>

        {/* Column legend */}
        <div style={{ display: 'grid', gridTemplateColumns: '64px 28px 1fr 60px 70px 80px 36px', gap: 8, alignItems: 'center', fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--ink-faint)', padding: '0 4px 8px' }}>
          <span>Ordning</span>
          <span />
          <span>Namn</span>
          <span>Färg</span>
          <span>Standard</span>
          <span>Visas</span>
          <span />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {statuses.map((step, i) => {
            const isAnchor    = (ANCHOR_STATUS_IDS as readonly string[]).includes(step.id)
            const unhideable  = UNHIDEABLE_STATUS_IDS.includes(step.id)
            const visible     = !localHidden.includes(step.id)
            return (
              <div
                key={step.id}
                style={{ display: 'grid', gridTemplateColumns: '64px 28px 1fr 60px 70px 80px 36px', gap: 8, alignItems: 'center', padding: '6px 4px', borderTop: '.5px solid var(--border)' }}
              >
                {/* Reorder */}
                <div style={{ display: 'flex', gap: 2 }}>
                  <button type="button" className="icon-btn" style={{ padding: 4 }} disabled={i === 0} onClick={() => moveStatus(i, -1)} aria-label="Flytta upp">
                    <ChevronUp size={14} />
                  </button>
                  <button type="button" className="icon-btn" style={{ padding: 4 }} disabled={i === statuses.length - 1} onClick={() => moveStatus(i, 1)} aria-label="Flytta ner">
                    <ChevronDown size={14} />
                  </button>
                </div>

                {/* Colour swatch */}
                <span style={{ width: 12, height: 12, borderRadius: '50%', background: statusDotColor(step), flexShrink: 0, justifySelf: 'center' }} />

                {/* Name */}
                <input
                  className="field-input"
                  style={{ padding: '4px 8px', fontSize: 13 }}
                  value={step.label}
                  onChange={e => updateStatus(step.id, { label: e.target.value, kort: e.target.value })}
                  placeholder="Statusnamn"
                />

                {/* Hue */}
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={step.hue}
                  onChange={e => updateStatus(step.id, { hue: Number(e.target.value) })}
                  style={{ accentColor: statusDotColor(step), width: '100%' }}
                  aria-label="Färgton"
                />

                {/* Default */}
                <input
                  type="radio"
                  name="default-status"
                  checked={localDefault === step.id}
                  onChange={() => setLocalDefault(step.id)}
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15, justifySelf: 'center' }}
                  aria-label="Standardstatus för nya ÄTA"
                />

                {/* Visible */}
                <input
                  type="checkbox"
                  checked={visible}
                  disabled={unhideable}
                  onChange={() =>
                    setLocalHidden(prev =>
                      prev.includes(step.id) ? prev.filter(id => id !== step.id) : [...prev, step.id]
                    )
                  }
                  style={{ accentColor: 'var(--accent)', width: 15, height: 15, justifySelf: 'center', opacity: unhideable ? 0.5 : 1 }}
                  aria-label="Visa i statusflödet"
                />

                {/* Delete */}
                <button
                  type="button"
                  className="icon-btn"
                  style={{ padding: 4, justifySelf: 'center', opacity: isAnchor ? 0.3 : 1 }}
                  disabled={isAnchor}
                  onClick={() => removeStatus(step.id)}
                  title={isAnchor ? 'Inbyggd status kan inte tas bort' : 'Ta bort status'}
                  aria-label="Ta bort status"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            )
          })}
        </div>

        <button type="button" className="icon-btn" style={{ marginTop: 12 }} onClick={addStatus}>
          <Plus size={14} /> Lägg till status
        </button>
      </SettingsCard>

      {/* 05 ÄTA-inställningar (full width) */}
      <SettingsCard nr="05" title="ÄTA-inställningar" fullWidth>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
          <Field label="Prefix" mono>
            <input className="field-input" value={p.ataNrPrefix ?? 'ÄTA'} onChange={e => set('ataNrPrefix', e.target.value)} placeholder="ÄTA" />
          </Field>
          <Field label="Nästa nummer" mono>
            <input className="field-input" type="number" value={p.ataNastaNum ?? ''} onChange={e => set('ataNastaNum', e.target.value)} placeholder="1" />
          </Field>
          <Field label="Standardpåslag" mono>
            <input className="field-input" value={p.standardPaslag ?? ''} onChange={e => set('standardPaslag', e.target.value)} placeholder="t.ex. 22,08 %" />
            <span className="field-hint">Centraladm + vinst &amp; risk</span>
          </Field>
          <Field label="Skapa underrättelse automatiskt">
            <select className="field-input" value={p.autoUnderr ?? 'ja'} onChange={e => set('autoUnderr', e.target.value)}>
              <option value="ja">Ja, alltid</option>
              <option value="nej">Nej, manuellt</option>
            </select>
          </Field>
        </div>

        <Field label="Mall för underrättelsetext" full>
          <div style={{ background: 'var(--bg-2)', borderRadius: 8, padding: '2px', marginTop: 4 }}>
            <textarea
              className="field-input"
              rows={7}
              style={{ background: 'transparent', border: 'none', resize: 'vertical', fontFamily: 'var(--mono)', fontSize: 12.5, lineHeight: 1.6 }}
              value={p.underrMall ?? ''}
              onChange={e => set('underrMall', e.target.value)}
              placeholder={`Härmed underrättas Ni i enlighet med ABT 06 kap. 2 § 6 om att följande ÄTA-arbete tillkommit:\n\nArbete: [Arbete]\nOrsak: [Orsak och beskrivning]\n\nPåverkan tid: [Påverkan tid]\nPåverkan ekonomi: [Påverkan ekonomi]\n\nSpecificerad kostnadsberäkning bifogas/överlämnas snarast.`}
            />
          </div>
          <span className="field-hint">Tokens: [Arbete], [Orsak och beskrivning], [Påverkan tid], [Påverkan ekonomi]</span>
        </Field>
      </SettingsCard>
    </div>
  )
}

interface SettingsCardProps {
  nr: string
  title: string
  children: ReactNode
  fullWidth?: boolean
}

function SettingsCard({ nr, title, children, fullWidth }: SettingsCardProps) {
  return (
    <div className="card" style={{ gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, paddingBottom: 14, borderBottom: '.5px solid var(--border)' }}>
        <span style={{
          width: 24, height: 24, borderRadius: 6, background: 'var(--bg-2)',
          border: '.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontFamily: 'var(--mono)', fontSize: 11, fontWeight: 600, color: 'var(--ink-dim)', flexShrink: 0,
        }}>{nr}</span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{title}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {children}
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  children: ReactNode
  full?: boolean
  mono?: boolean
}

function Field({ label, children, full }: FieldProps) {
  return (
    <div className="field" style={{ gridColumn: full ? '1 / -1' : undefined }}>
      <label className="field-label">{label}</label>
      {children}
    </div>
  )
}
